import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, TextField, Typography, MenuItem } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = '/squad-players/';
const SQUADS_API_URL = '/squads/';
const PLAYERS_API_URL = '/players/';

const positionOptions = [
  { value: 'GK', label: 'Goalkeeper' },
  { value: 'DEF', label: 'Defender' },
  { value: 'MID', label: 'Midfielder' },
  { value: 'FWD', label: 'Forward' },
];

const SquadPlayerForm: React.FC = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [squads, setSquads] = useState<{ id: number; team: { name: string }; league: { name: string } }[]>([]);
  const [players, setPlayers] = useState<{ id: number; name: string }[]>([]);
  const [form, setForm] = useState({
    squad: '',
    player: '',
    position: '',
    status: '',
    credits: '',
    points: '',
  });

  useEffect(() => {
    axios.get(SQUADS_API_URL).then(res => setSquads(res.data)).catch(() => setSquads([]));
    axios.get(PLAYERS_API_URL).then(res => setPlayers(res.data)).catch(() => setPlayers([]));
    if (isEdit) {
      setLoading(true);
      axios.get(API_URL + id + '/').then(res => {
        setForm({
          squad: res.data.squad ? res.data.squad.id : '',
          player: res.data.player ? res.data.player.id : '',
          position: res.data.position || '',
          status: res.data.status || '',
          credits: res.data.credits || '',
          points: res.data.points || '',
        });
      }).catch(() => setError('Failed to load squad player')).finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = { ...form, squad: form.squad || null, player: form.player || null };
      if (isEdit) {
        await axios.put(API_URL + id + '/', payload);
        setSuccess('Squad Player updated successfully');
      } else {
        await axios.post(API_URL, payload);
        setSuccess('Squad Player created successfully');
        setForm({ squad: '', player: '', position: '', status: '', credits: '', points: '' });
      }
      setTimeout(() => navigate('/football/squad-players'), 1000);
    } catch (err: any) {
      setError('Failed to save squad player');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxWidth={500} mx="auto" mt={4}>
      <Typography variant="h5" mb={2}>{isEdit ? 'Edit' : 'Create'} Football Squad Player</Typography>
      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField select label="Squad" name="squad" value={form.squad} onChange={handleChange} required fullWidth>
            <MenuItem value="">None</MenuItem>
            {squads.map(squad => <MenuItem key={squad.id} value={squad.id}>{squad.team?.name || ''} ({squad.league?.name || ''})</MenuItem>)}
          </TextField>
          <TextField select label="Player" name="player" value={form.player} onChange={handleChange} required fullWidth>
            <MenuItem value="">None</MenuItem>
            {players.map(player => <MenuItem key={player.id} value={player.id}>{player.name}</MenuItem>)}
          </TextField>
          <TextField select label="Position" name="position" value={form.position} onChange={handleChange} fullWidth>
            <MenuItem value="">None</MenuItem>
            {positionOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
          </TextField>
          <TextField label="Status" name="status" value={form.status} onChange={handleChange} fullWidth />
          <TextField label="Credits" name="credits" value={form.credits} onChange={handleChange} type="number" fullWidth />
          <TextField label="Points" name="points" value={form.points} onChange={handleChange} type="number" fullWidth />
          {error && <Typography color="error">{error}</Typography>}
          {success && <Typography color="success.main">{success}</Typography>}
          <Button type="submit" variant="contained" disabled={loading}>{isEdit ? 'Update' : 'Create'}</Button>
        </Stack>
      </form>
    </Box>
  );
};

export default SquadPlayerForm; 